// ============================================================================
// AUDIT FIRM MASTER SCREEN — FSA Signature Workflow
// ============================================================================
// Lists audit firms from fsa.tbl_audit_firm. Standard CRUD with
// SyutGrid + filter panel + Add/Edit form modal + Delete ConfirmDialog.
// Sister screens to follow same shape:
//   - FsaAuditPartnerMasterScreen (drilled-in from a firm row)
//   - Director master tab on Client master
//   - FsaEngagementSignaturePanelScreen (per-engagement)

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useFsaAuditFirms } from 'hooks/api/fsa/useFsaSignature';
import { fsaAuditFirmService } from 'api/services/fsa/fsaSignatureService';
import type { AuditFirm, AuditFirmSearchRequest } from 'api/types/fsa/signature.types';
import AuditFirmFormModal from 'components/modals/modules/fsa/AuditFirmFormModal';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
};

interface FsaAuditFirmMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const FsaAuditFirmMasterScreen: React.FC<FsaAuditFirmMasterScreenProps> = () => {
  // Server-side pagination + filter state
  const [queryParams, setQueryParams] = useState<AuditFirmSearchRequest>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useFsaAuditFirms(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [firmNameFilter, setFirmNameFilter] = useState('');
  const [frnFilter, setFrnFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Server-side search debounce.
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handlePageChange = useCallback((idx: number) => {
    setQueryParams(prev => ({ ...prev, page: idx + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams(prev => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      firmName:               firmNameFilter || undefined,
      firmRegistrationNumber: frnFilter || undefined,
      statusCode:             statusFilter || undefined,
      includeInactive:        statusFilter === 'INACTIVE',
      page:                   1,
    }));
    setFilterOpen(false);
  }, [firmNameFilter, frnFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setFirmNameFilter('');
    setFrnFilter('');
    setStatusFilter('');
    setQueryParams(prev => ({
      ...prev,
      firmName: undefined, firmRegistrationNumber: undefined, statusCode: undefined, includeInactive: undefined, page: 1,
    }));
  }, []);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedFirm, setSelectedFirm] = useState<AuditFirm | null>(null);

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Columns
  const columns: SyutGridColumn<AuditFirm>[] = useMemo(() => [
    { id: 'firmName', header: 'Firm Name', accessorKey: 'firmName', enableSorting: true, size: 280 },
    { id: 'frn', header: 'FRN', accessorKey: 'firmRegistrationNumber', enableSorting: true, size: 180 },
    { id: 'partnerCount', header: 'Partners',
      accessorFn: (row: AuditFirm) => row.partnerCount,
      cell: ({ row }: { row: { original: AuditFirm } }) => <span style={{ display: 'inline-block', minWidth: 28, textAlign: 'center', padding: '0.125rem 0.375rem', borderRadius: 4, background: 'var(--bg-secondary)', fontSize: '0.8125rem' }}>{row.original.partnerCount}</span>,
      size: 80, enableSorting: false },
    { id: 'email', header: 'Email', accessorKey: 'email', enableSorting: false, size: 200 },
    { id: 'phone', header: 'Phone', accessorKey: 'phone', enableSorting: false, size: 120 },
    { id: 'status', header: 'Status', accessorKey: 'statusName', enableSorting: true, size: 100 },
    { id: 'createdAt', header: 'Created',
      accessorFn: (row: AuditFirm) => row.createdAt,
      cell: ({ row }: { row: { original: AuditFirm } }) => <span>{formatDate(row.original.createdAt)}</span>,
      size: 110, enableSorting: true },
  ], []);

  // Row actions
  const handleEdit = useCallback((row: AuditFirm) => {
    setSelectedFirm(row);
    setFormMode('edit');
    setFormModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((row: AuditFirm) => {
    setSelectedFirm(row);
    setDeleteModalOpen(true);
  }, []);

  const getRowActions = useCallback((row: AuditFirm): ActionMenuItem[] => [
    { id: 'edit',   label: 'Edit',   icon: <IconSystem.edit  size={16} />, onClick: () => handleEdit(row) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => handleDeleteClick(row), variant: 'danger' },
  ], [handleEdit, handleDeleteClick]);

  // Action buttons
  const handleAdd = useCallback(() => {
    setSelectedFirm(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, []);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add',     icon: <IconSystem.add     size={16} />, label: 'Add',     onClick: handleAdd, variant: 'primary' },
    { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => refetch(), variant: 'icon' },
  ], [handleAdd, refetch]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedFirm) return;
    setIsDeleting(true);
    try {
      await fsaAuditFirmService.remove(selectedFirm.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedFirm.firmName}" deleted successfully.`);
      setSelectedFirm(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Delete failed.', 6000);
    }
  }, [selectedFirm, refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Audit Firms"
        description="Firm-wide audit firm master. Used by FSA Signature Workflow to populate the auditor side of signed Financial Statements."
        icon={<IconSystem.users size={20} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search firms…',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              enableSorting
              rowActions={getRowActions}
              emptyMessage={loading ? 'Loading…' : 'No audit firms found'}
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Firm Name</label>
                <input type="text" className="form-input" value={firmNameFilter}
                  onChange={(e) => setFirmNameFilter(e.target.value)}
                  placeholder="Filter by firm name…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>FRN</label>
                <input type="text" className="form-input" value={frnFilter}
                  onChange={(e) => setFrnFilter(e.target.value)}
                  placeholder="Filter by registration number…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={(val) => setStatusFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Delete audit firm "${selectedFirm?.firmName}"? Existing engagement signature panels that reference this firm will keep their snapshot data, but new engagements won't be able to select it.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />

      <AuditFirmFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedFirm}
        onSaved={refetch}
        onClose={() => setFormModalOpen(false)}
      />
    </div>
  );
};

export default FsaAuditFirmMasterScreen;
